/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListDismissedRiskResultsRequest,
  ListDismissedRiskResultsSecurity,
  Reasons,
} from "../models/operations/listdismissedriskresults.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRiskListDismissedResultsQuery,
  prefetchRiskListDismissedResults,
  queryKeyRiskListDismissedResults,
  RiskListDismissedResultsQueryData,
} from "./riskListDismissedResults.core.js";
export {
  buildRiskListDismissedResultsQuery,
  prefetchRiskListDismissedResults,
  queryKeyRiskListDismissedResults,
  type RiskListDismissedResultsQueryData,
};

export type RiskListDismissedResultsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listDismissedRiskResults risk
 *
 * @remarks
 * List suppressed risk results for the current project — findings hidden by an exclusion rule, a manual dismissal, or the automated false-positive sweep. Kept separate from listRiskResults, which never returns suppressed results.
 */
export function useRiskListDismissedResults(
  request?: ListDismissedRiskResultsRequest | undefined,
  security?: ListDismissedRiskResultsSecurity | undefined,
  options?: QueryHookOptions<
    RiskListDismissedResultsQueryData,
    RiskListDismissedResultsQueryError
  >,
): UseQueryResult<
  RiskListDismissedResultsQueryData,
  RiskListDismissedResultsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildRiskListDismissedResultsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listDismissedRiskResults risk
 *
 * @remarks
 * List suppressed risk results for the current project — findings hidden by an exclusion rule, a manual dismissal, or the automated false-positive sweep. Kept separate from listRiskResults, which never returns suppressed results.
 */
export function useRiskListDismissedResultsSuspense(
  request?: ListDismissedRiskResultsRequest | undefined,
  security?: ListDismissedRiskResultsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RiskListDismissedResultsQueryData,
    RiskListDismissedResultsQueryError
  >,
): UseSuspenseQueryResult<
  RiskListDismissedResultsQueryData,
  RiskListDismissedResultsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRiskListDismissedResultsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRiskListDismissedResultsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      reasons?: Array<Reasons> | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RiskListDismissedResultsQueryData,
): RiskListDismissedResultsQueryData | undefined {
  const key = queryKeyRiskListDismissedResults(...queryKeyBase);

  return client.setQueryData<RiskListDismissedResultsQueryData>(key, data);
}

export function invalidateRiskListDismissedResults(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      reasons?: Array<Reasons> | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "results", "listDismissed", ...queryKeyBase],
  });
}

export function invalidateAllRiskListDismissedResults(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "results", "listDismissed"],
  });
}
